"use client";

import { useCallback, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@workspace/ui/components/dialog";
import { Button } from "@workspace/ui/components/button";
import { Badge } from "@workspace/ui/components/badge";
import { Input } from "@workspace/ui/components/input";
import {
  Upload,
  FileText,
  FileCode,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Link,
  Globe,
} from "lucide-react";
import { apiClient } from "@/lib/api-client";

const ACCEPTED_EXTENSIONS = ".md,.markdown,.txt,.html,.htm,.docx";
const MAX_FILE_SIZE = 10 * 1024 * 1024;

type ImportMode = "file" | "url";

function formatIcon(name: string) {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "docx") return <FileText className="h-4 w-4 text-blue-500" />;
  if (ext === "html" || ext === "htm")
    return <FileCode className="h-4 w-4 text-orange-500" />;
  return <FileText className="h-4 w-4 text-muted-foreground" />;
}

function formatBadge(name: string) {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const labels: Record<string, string> = {
    md: "Markdown",
    markdown: "Markdown",
    txt: "Text",
    html: "HTML",
    htm: "HTML",
    docx: "DOCX",
  };
  return labels[ext] ?? ext.toUpperCase();
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ImportResult {
  id: string;
  title: string;
  filename: string;
  markdown: string;
}

interface ImportError {
  filename: string;
  error: string;
}

interface UrlImportResult {
  id: string;
  title: string;
  markdown: string;
  metadata: {
    sourceUrl: string;
    author: string | null;
    publishedDate: string | null;
    description: string | null;
    siteName: string | null;
  };
}

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId?: string;
  onImported?: () => void;
}

export function ImportDialog({
  open,
  onOpenChange,
  collectionId,
  onImported,
}: ImportDialogProps) {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<ImportMode>("file");
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportResult[] | null>(null);
  const [errors, setErrors] = useState<ImportError[]>([]);

  // URL import state
  const [url, setUrl] = useState("");
  const [urlResult, setUrlResult] = useState<UrlImportResult | null>(null);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const arr = Array.from(newFiles).filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return (
        ext &&
        ["md", "markdown", "txt", "html", "htm", "docx"].includes(ext) &&
        f.size <= MAX_FILE_SIZE
      );
    });
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const unique = arr.filter(
        (f) => !existing.has(`${f.name}-${f.size}`)
      );
      return [...prev, ...unique];
    });
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleFileImport = async () => {
    if (files.length === 0) return;
    setImporting(true);
    setErrors([]);

    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }
      if (collectionId) {
        formData.append("collectionId", collectionId);
      }

      const result = await apiClient.safeRequest<{
        imported: ImportResult[];
        errors?: ImportError[];
      }>("/api/documents/import", {
        method: "POST",
        body: formData,
      });

      if (!result.ok) {
        const errData = (result.error.responseData ?? {}) as {
          error?: string;
        };
        setErrors([{ filename: "", error: errData.error || "Import failed" }]);
        return;
      }

      const data = result.data;
      setResults(data.imported);
      setErrors(data.errors ?? []);
      onImported?.();

      // Store markdown in sessionStorage so the editor can initialize
      for (const item of data.imported) {
        sessionStorage.setItem(`docs:import:${item.id}`, item.markdown);
      }

      // If exactly one file imported, navigate to it
      if (data.imported.length === 1) {
        onOpenChange(false);
        router.push(`/${data.imported[0].id}`);
      }
    } catch (err) {
      setErrors([
        {
          filename: "",
          error: err instanceof Error ? err.message : "Import failed",
        },
      ]);
    } finally {
      setImporting(false);
    }
  };

  const handleUrlImport = async () => {
    if (!url.trim()) return;
    setImporting(true);
    setErrors([]);
    setUrlResult(null);

    try {
      const result = await apiClient.safeRequest<UrlImportResult>(
        "/api/documents/import-url",
        {
          method: "POST",
          body: { url: url.trim(), collectionId },
        },
      );

      if (!result.ok) {
        const errData = (result.error.responseData ?? {}) as {
          error?: string;
        };
        setErrors([{ filename: url, error: errData.error || "Import failed" }]);
        return;
      }

      const data = result.data;

      // Store markdown in sessionStorage
      sessionStorage.setItem(`docs:import:${data.id}`, data.markdown);

      setUrlResult(data);
      onImported?.();

      // Navigate after short delay so user can see the result
      setTimeout(() => {
        onOpenChange(false);
        router.push(`/${data.id}`);
      }, 1500);
    } catch (err) {
      setErrors([
        {
          filename: url,
          error: err instanceof Error ? err.message : "Import failed",
        },
      ]);
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFiles([]);
    setResults(null);
    setErrors([]);
    setUrl("");
    setUrlResult(null);
  };

  const handleClose = (open: boolean) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const showResults = results || urlResult;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Documents</DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        {!showResults && (
          <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                mode === "file"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setMode("file")}
            >
              <Upload className="h-3.5 w-3.5 inline mr-1.5" />
              File
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                mode === "url"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setMode("url")}
            >
              <Link className="h-3.5 w-3.5 inline mr-1.5" />
              URL
            </button>
          </div>
        )}

        {/* URL import result */}
        {urlResult && (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {urlResult.title}
                </p>
                {urlResult.metadata.siteName && (
                  <p className="text-xs text-muted-foreground">
                    {urlResult.metadata.siteName}
                  </p>
                )}
                {urlResult.metadata.author && (
                  <p className="text-xs text-muted-foreground">
                    by {urlResult.metadata.author}
                  </p>
                )}
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {urlResult.metadata.sourceUrl}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Redirecting to document...
            </p>
          </div>
        )}

        {/* File import results */}
        {results && (
          <div className="space-y-3 py-2">
            {results.map((r) => (
              <button
                type="button"
                key={r.id}
                className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30 cursor-pointer hover:bg-muted/60 transition-colors w-full text-left"
                onClick={() => {
                  handleClose(false);
                  router.push(`/${r.id}`);
                }}
              >
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{r.title}</p>
                  <p className="text-xs text-muted-foreground">{r.filename}</p>
                </div>
              </button>
            ))}
            {errors.map((e, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-lg border border-destructive/20 bg-destructive/5"
              >
                <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {e.filename || "Error"}
                  </p>
                  <p className="text-xs text-destructive">{e.error}</p>
                </div>
              </div>
            ))}
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* File import mode */}
        {!showResults && mode === "file" && (
          <>
            <button
              type="button"
              className={`
                relative border-2 border-dashed rounded-lg p-8 w-full
                flex flex-col items-center justify-center gap-3
                transition-colors cursor-pointer
                ${dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
              `}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-center">
                <span className="text-sm font-medium block">
                  Drop files here or click to browse
                </span>
                <span className="text-xs text-muted-foreground mt-1 block">
                  Markdown, HTML, DOCX — up to 10 MB each
                </span>
              </span>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </button>

            {files.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {files.map((file, i) => (
                  <div
                    key={`${file.name}-${i}`}
                    className="flex items-center gap-3 p-2 rounded-md border"
                  >
                    {formatIcon(file.name)}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatSize(file.size)}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {formatBadge(file.name)}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => removeFile(i)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {errors.length > 0 && (
              <div className="space-y-1">
                {errors.map((e, i) => (
                  <p key={i} className="text-xs text-destructive">
                    {e.filename ? `${e.filename}: ` : ""}
                    {e.error}
                  </p>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleFileImport}
                disabled={files.length === 0 || importing}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import {files.length > 0 ? `(${files.length})` : ""}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* URL import mode */}
        {!showResults && mode === "url" && (
          <>
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-4 rounded-lg border border-dashed border-muted-foreground/25">
                <Globe className="h-5 w-5 text-muted-foreground shrink-0" />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && url.trim()) handleUrlImport();
                  }}
                  placeholder="https://example.com/article"
                  className="border-0 focus-visible:ring-0 h-auto p-0 text-sm"
                  autoFocus
                  disabled={importing}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Paste a link to an article or web page. Content will be extracted
                and converted to a document, preserving metadata like author and
                publish date.
              </p>
            </div>

            {errors.length > 0 && (
              <div className="space-y-1">
                {errors.map((e, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded-md border border-destructive/20 bg-destructive/5"
                  >
                    <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                    <p className="text-xs text-destructive">{e.error}</p>
                  </div>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUrlImport}
                disabled={!url.trim() || importing}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Scraping...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4 mr-2" />
                    Import
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
